<template>
	<v-container fluid>
		<!-- headers for the month -->
		<v-layout row>
			<v-flex>
				<v-card flat tile class="transparent">
					<v-card-title class="justify-center">
						<h1>
							<b>{{ currentMonthAndYear }}</b>
						</h1>
					</v-card-title>
				</v-card>
			</v-flex>
		</v-layout>
		<!-- headers for day of week -->
		<v-layout row class="week-grid">
			<v-flex v-for="weekday in weekdays" :key="weekday">
				<v-card flat tile class="mx-1 dark-grey">
					<v-card-title class="pa-0 justify-center">
						<h2>
							<b>{{ weekday }}</b>
						</h2>
					</v-card-title>
				</v-card>
			</v-flex>
		</v-layout>

		<!-- loop the weeks of the month -->
		<v-layout
			row
			v-for="(week, wIndex) in weeks"
			:key="'week-' + wIndex"
			class="week-grid my-2"
		>
			<!-- loop the days of the week -->
			<v-flex v-for="(day, index) in week" :key="index">
				<v-hover v-slot:default="{ hover }">
					<v-card
						flat
						tile
						v-show="day.isCurrentMonth"
						:class="{
							'elevation-10': hover,
							'fertiger-holiday': day.isFertigerHoliday,
							'public-holiday': !day.isFertigerHoliday && day.isPublicHoliday,
							weekend: !isHoliday(day) && day.isWeekend,
							'ma-1': true,
						}"
						style="height: 100%; min-height: 160px"
					>
						<v-card-title class="pa-1 justify-center">
							<h2>{{ day.display }}</h2>
						</v-card-title>
						<v-card-text class="pa-1 text-xs-center">
							<v-layout column fill-height>
								<v-flex style="height: 20px">
									<span v-if="day.isPublicHoliday">{{
										day.publicHolidayName
									}}</span>
								</v-flex>

								<v-flex style="height: 40px">
									<v-layout row align-center justify-center fill-height>
										<v-flex
											v-if="day.isProductionDay && !day.isFertigerHoliday"
											shrink
										>
											<v-btn icon class="production-day" title="Produktion">
												<v-icon>engineering</v-icon>
											</v-btn>
										</v-flex>
										<v-flex
											v-if="day.isDeliveryDay && !day.isFertigerHoliday"
											shrink
										>
											<v-btn icon class="delivery-day" title="Liefertag">
												<v-icon>local_shipping</v-icon>
											</v-btn>
										</v-flex>
									</v-layout>
								</v-flex>

								<v-flex style="height: 20px">
									<span class="closed-text" v-if="isHoliday(day)">
										geschlossen
									</span>
								</v-flex>

								<v-flex style="height: 40px">
									<v-btn
										v-if="
											hover &&
											!day.isWeekend &&
											!day.isPublicHoliday &&
											!isInThePast(day.date)
										"
										:title="
											day.isFertigerHoliday
												? 'Klicken, um Betrieb als geöffnet zu markieren'
												: 'Klicken, um den Betrieb an diesem Tag als geschlossen zu markieren. Beispielsweise weil die Maschine in Revision ist, Betriebsferien oder lokale Feiertage stattfinden.'
										"
										small
										:disabled="isSavingHoliday"
										@click="toggleHoliday(day.date)"
									>
										<v-progress-circular
											size="16"
											v-if="isSavingHoliday"
											indeterminate
										></v-progress-circular>
										<span v-if="day.isFertigerHoliday">öffnen</span>
										<span v-else>schließen</span>
									</v-btn>
								</v-flex>
							</v-layout>
						</v-card-text>
					</v-card>
				</v-hover>
			</v-flex>
		</v-layout>
		<v-dialog
			v-if="cannotEditWerkkalenderDialog"
			v-model="cannotEditWerkkalenderDialog"
			width="500"
		>
			<v-card>
				<v-card-title class="headline grey lighten-2" primary-title>
					Keine Berechtigung
				</v-card-title>
				<v-card-text>
					Sie haben keine Berechtigung den Werkskalender zu verändern.<br />
					Wenden Sie sich an eine der folgenden Personen in Ihrem Betrieb. Diese verfügen
					über die Berechtigung.<br /><br />
					<ul>
						<li v-for="(login, index) in loginsWithEditRights" :key="index">
							{{ login }}
						</li>
					</ul>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn class="blexon-button" flat @click="cannotEditWerkkalenderDialog = false">
						OK
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import moment from "moment";
import httpClient from "../store/httpClient";

export default {
	props: {
		monthData: {},
	},
	data() {
		return {
			calendarDays: [],
			weekdays: ["MO", "DI", "MI", "DO", "FR", "SA", "SO"],
			months: [
				"Januar",
				"Februar",
				"März",
				"April",
				"Mai",
				"Juni",
				"Juli",
				"August",
				"September",
				"Oktober",
				"November",
				"Dezember",
			],
			isSavingHoliday: false,
			cannotEditWerkkalenderDialog: false,
			loginsWithEditRights: [],
		};
	},
	mounted() {
		this.generateCalendar();
		this.getLoginsWithEditRights();
	},
	computed: {
		weeks() {
			const weeks = [];
			const totalWeeks = Math.ceil(this.calendarDays.length / 7);
			for (let i = 0; i < totalWeeks; i++) {
				weeks.push(this.calendarDays.slice(i * 7, (i + 1) * 7));
			}
			return weeks;
		},
		currentMonthAndYear() {
			return `${this.months[this.monthData.date.getMonth()]} ${this.monthData.date.getFullYear()}`;
		},
		currentFertiger() {
			var fertigerId = this.$route.params.fertigerId;
			return this.$store.state.fertiger.filter((f) => f.id == fertigerId)[0];
		},
	},
	methods: {
		generateCalendar() {
			const year = this.monthData.date.getFullYear();
			const month = this.monthData.date.getMonth();
			const firstDayOfMonth = new Date(year, month, 1);
			const lastDayOfMonth = new Date(year, month + 1, 0);

			const startDayOfWeek = firstDayOfMonth.getDay();
			const totalDaysInMonth = lastDayOfMonth.getDate();

			const previousMonthDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

			this.calendarDays = [];

			for (let i = previousMonthDays; i > 0; i--) {
				const day = new Date(year, month, -i + 1);
				this.calendarDays.push({
					date: day,
					display: day.getDate().toString().padStart(2, "0"),
					isCurrentMonth: false,
					isWeekend: this.isWeekend(day),
					isDeliveryDay: false,
					isProductionDay: false,
					isPublicHoliday: false,
					isFertigerHoliday: false,
				});
			}

			for (let i = 1; i <= totalDaysInMonth; i++) {
				const day = new Date(year, month, i);
				this.calendarDays.push({
					date: day,
					display: i.toString().padStart(2, "0"),
					isCurrentMonth: true,
					isWeekend: this.isWeekend(day),
					isDeliveryDay: this.isDeliveryDay(day),
					isProductionDay: this.isProductionDay(day),
					isPublicHoliday: this.getPublicHolidayName(day).length > 0,
					publicHolidayName: this.getPublicHolidayName(day),
					isFertigerHoliday: this.isFertigerHoliday(day),
				});
			}

			const totalTiles = this.calendarDays.length;
			const nextMonthDays = (7 - (totalTiles % 7)) % 7;

			for (let i = 1; i <= nextMonthDays; i++) {
				const day = new Date(year, month + 1, i);
				this.calendarDays.push({
					date: day,
					display: i.toString().padStart(2, "0"),
					isCurrentMonth: false,
					isWeekend: this.isWeekend(day),
					isDeliveryDay: false,
					isProductionDay: false,
					isPublicHoliday: false,
					isFertigerHoliday: false,
				});
			}
		},
		isWeekend(date) {
			return date.getDay() === 0 || date.getDay() === 6; // Sunday = 0, Saturday = 6
		},
		isDeliveryDay(date) {
			return this.monthData.deliveryDays.some((dd) => dd.getDate() == date.getDate());
		},
		isProductionDay(date) {
			return this.monthData.productionDays.some((pd) => pd.getDate() == date.getDate());
		},
		isFertigerHoliday(date) {
			return this.monthData.fertigerHolidays.some((pd) => pd.getDate() == date.getDate());
		},
		isInThePast(date) {
			return moment(date).isBefore(moment());
		},
		getPublicHolidayName(date) {
			let holidays = this.monthData.publicHolidays.filter(
				(ph) => ph.date.getDate() == date.getDate()
			);
			return holidays.length > 0 ? holidays[0].name : "";
		},

		isHoliday(day) {
			return day.isPublicHoliday || day.isFertigerHoliday;
		},

		canEditPlan() {
			// Admin und Helpdesk sind im dem Property schon serverseitig berücksichtigt
			if (this.currentFertiger.canEditProgrammingData) {
				return true;
			}
			return false;
		},

		async toggleHoliday(date) {
			if (!this.canEditPlan()) {
				this.cannotEditWerkkalenderDialog = true;
				return;
			}
			let dateIndex = this.calendarDays.findIndex((cd) =>
				moment(cd.date).isSame(date, "day")
			);
			if (dateIndex <= -1) {
				console.log("Day could not be found.", date);
				return;
			}
			try {
				this.isSavingHoliday = true;
				let utcDate = new Date(
					Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
				);
				let stringDate = utcDate.toISOString().split("T")[0];
				const response = await httpClient().post(
					`api/Capacity/ToggleHoliday?fertigerId=${this.$route.params.fertigerId}&date=${stringDate}`
				);
				const day = this.calendarDays[dateIndex];
				day.isFertigerHoliday = !day.isFertigerHoliday;
				day.isProductionDay = response.data & 2;
				day.isDeliveryDay = response.data & 1;
			} catch {
				this.$emit("showSnackbar", "Ferientag konnte nicht gespeichert werden.");
			} finally {
				this.isSavingHoliday = false;
			}
		},

		async getLoginsWithEditRights() {
			try {
				const response = await httpClient().get(
					`api/Fertiger/EditFertiger?id=${this.$route.params.fertigerId}`
				);
				this.loginsWithEditRights = response.data.logins
					.filter((l) => l.canEditProgrammingData)
					.map((l) => l.username);
			} catch {
				this.$emit("showSnackbar", "Fehler beim Laden der Logindaten.");
			}
		},
	},
};
</script>

<style scoped>
.week-grid {
	display: grid;
	grid-template-columns: repeat(7, 1fr); /* 7 equal columns */
}
.weekend {
	background-color: #cfd8dc;
}
.dark-grey {
	background-color: grey;
	color: white;
}
.public-holiday {
	background-color: #ffe0b2;
}
.fertiger-holiday {
	background-color: #ffccbc;
}
button.delivery-day {
	background-color: #fff59d !important;
	color: #f57f17 !important;
}
button.production-day {
	background-color: #90caf9 !important;
	color: #0d47a1 !important;
}
.closed-text {
	color: brown;
	font-weight: bold;
}
</style>
